import React from 'react';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import { differenceInCalendarDays } from 'date-fns';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import DetailsCard from '@rental-manager/components/DetailsCard';
import RentAmountChangeStatus from '@rental-manager/constants/RentAmountChangeConstants';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import { coerceToDate } from '@common/paymentsUtils/dateUtils';
import StringConstants from '@common/constants/StringConstants';
import HelpCircleFill from '@zg-rentals/multibrand-ui/lib/icons/HelpCircleFill';

type Props = {
  today: any;
  rentTerms: Record<string, any>;
  goToEditRentAmountPage: (...args: Array<any>) => any;
  cancelRentAmountChange: (...args: Array<any>) => any;
};

class MonthlyAmount extends React.Component<Props> {
  cancelRentAmountChange = (): void => {
    const { tenantFirstName, rentTermsKey } = this.props.rentTerms;
    return this.props.cancelRentAmountChange({ tenantFirstName, rentTermsKey });
  };

  renderRentAmountChangeSection = (): React.ReactNode => {
    const { today, rentTerms } = this.props;
    const { tenantFirstName, rentAmountChangeRequest = {} } = rentTerms;
    const { effectiveDueDate } = rentAmountChangeRequest;
    const effectiveDueDateFormatted = formatDate(effectiveDueDate, 'MMMM do');
    if (rentAmountChangeRequest.status === RentAmountChangeStatus.PENDING) {
      const daysLeftToAccept = rentAmountChangeRequest.effectiveDueDate
        ? differenceInCalendarDays(coerceToDate(rentAmountChangeRequest.effectiveDueDate)!, today)
        : undefined;
      return (
        <>
          <Text emphasize inline>
            Rent amount change started
          </Text>
          <Link href={StringConstants.LINKS.RENT_AMOUNT_CHANGE_FAQ.LANDLORD} target="_blank">
            <HelpCircleFill size="sm" colorType="text-secondary" className="HelpTooltip IconAlignment" />
          </Link>
          <Text>New amount: {centsToDollars(rentAmountChangeRequest.newAmount)}</Text>
          {daysLeftToAccept ? (
            <Text>
              Status: {tenantFirstName} has {daysLeftToAccept} days to accept
            </Text>
          ) : null}
          <Text>Effective: {effectiveDueDateFormatted}</Text>
          <Text>
            <Link onClick={this.cancelRentAmountChange}>Cancel request</Link>
          </Text>
        </>
      );
    }
    if (rentAmountChangeRequest.status === RentAmountChangeStatus.ACCEPTED) {
      return (
        <>
          <Text emphasize>Your upcoming rent amount update</Text>
          <Text>New amount: {centsToDollars(rentAmountChangeRequest.newAmount)}</Text>
          <Text>Status: {tenantFirstName} has accepted</Text>
          <Text>Effective: {effectiveDueDateFormatted}</Text>
          <Text>
            <Link onClick={this.cancelRentAmountChange}>Cancel request</Link>
          </Text>
        </>
      );
    }
    if (rentAmountChangeRequest.status === RentAmountChangeStatus.EXPIRED) {
      return (
        <>
          <Text emphasize inline>
            Rent amount change expired
          </Text>
          <Link href={StringConstants.LINKS.RENT_AMOUNT_CHANGE_FAQ.LANDLORD} target="_blank">
            <HelpCircleFill size="sm" colorType="text-secondary" className="HelpTooltip IconAlignment" />
          </Link>
          <Text>New amount: {centsToDollars(rentAmountChangeRequest.newAmount)}</Text>
          <Text>Status: Expired</Text>
          <Text>
            <Link onClick={this.cancelRentAmountChange}>Cancel request</Link>
          </Text>
        </>
      );
    }
    return <></>;
  };

  goToEditPage = (): void => {
    const { goToEditRentAmountPage, rentTerms } = this.props;
    goToEditRentAmountPage(rentTerms);
  };

  render(): React.ReactNode {
    const { amount, rentAmountChangeRequest = {} } = this.props.rentTerms;
    return (
      <DetailsCard
        title="Monthly rent"
        onEdit={this.goToEditPage}
        editingDisabled={rentAmountChangeRequest.status !== RentAmountChangeStatus.ENABLED}
      >
        <Text>{centsToDollars(amount)}</Text>
        {this.renderRentAmountChangeSection()}
      </DetailsCard>
    );
  }
}

export default MonthlyAmount;
